<script setup lang="ts">
import { computed } from "vue";
import { resolveImageAlt } from "@/utils/imageAlt";
import { resolvePageHeading } from "@/utils/pageHeading";

const props = defineProps<{
  flagshipGroupedFaqs?: Record<string, { faqs: any[]; catIcon: string }>;
  /** Full layouts from page fetch — omitted from usePage() slim state. */
  layouts?: unknown[] | null;
}>();

const page = usePage();
const introLayouts = computed(() =>
  Array.isArray(props.layouts)
    ? props.layouts
    : Array.isArray(page.value?.layouts)
      ? page.value.layouts
      : [],
);
const route = useRoute();
const isFlagship = computed(() => isFlagshipTrekPath(route.path));

const trekPackagesTitle = computed(
  () =>
    String(page.value?.meta?.title ?? "").trim() ||
    resolvePageHeading(page.value) ||
    String(page.value?.title ?? "").trim() ||
    String(page.value?.menuText ?? "").trim(),
);

type KeyFact = { label: string; value: string; icon: string };

/** Map Key fact labels to brand icons used by trekIntroStats (Top 10 / Distance / etc.). */
const KEY_FACT_ICONS: Record<string, string> = {
  Distance: "Distance",
  "Height gain": "Ascent Descent",
  Grade: "Trek Grade 3",
  "Duration / stages": "Duration",
  "Best months": "Dates",
  "Group size": "Group size",
};

function iconForGrade(grade: unknown): string {
  const match = String(grade ?? "").match(/([1-4])/);
  return match ? `Trek Grade ${match[1]}` : "Trek Grade 3";
}

const keyFacts = computed<KeyFact[]>(() => {
  const p = page.value;
  if (!isFlagship.value || !p) return [];

  const facts: KeyFact[] = [];
  const add = (label: string, value: unknown, icon?: string) => {
    const str = String(value ?? "").trim();
    if (str) {
      facts.push({
        label,
        value: str,
        icon: icon || KEY_FACT_ICONS[label] || "Info",
      });
    }
  };

  add("Distance", p.trek_distance);
  add("Height gain", p.trek_elevation);
  add("Grade", p.trek_grade, iconForGrade(p.trek_grade));
  add("Duration / stages", p.trek_duration || p.duration || p.trekDuration);
  add("Best months", p.trek_season || p.bestMonths || p.trekBestMonths);
  add("Group size", p.group_sizes || p.groupSize || p.trek_group_size);

  // Prefer canonical trek_* fields over trekIntroStats for the same metric
  // (CMS sometimes has 31,200ft in trek_elevation and 31,300ft under Ascent Descent).
  const coveredIcons = new Set<string>();
  if (p.trek_distance) coveredIcons.add("Distance");
  if (p.trek_elevation) coveredIcons.add("Ascent Descent");
  if (p.trek_grade) coveredIcons.add(iconForGrade(p.trek_grade));

  if (Array.isArray(p.trekIntroStats)) {
    for (const stat of p.trekIntroStats) {
      const text = String(stat?.statText ?? "").trim();
      const icon = String(stat?.statIcon ?? "").trim();
      if (!text) continue;
      if (icon && coveredIcons.has(icon)) continue;
      const alreadyHave = facts.some(
        (f) =>
          f.value === text ||
          f.value.toLowerCase().includes(text.toLowerCase()) ||
          text.toLowerCase().includes(f.value.toLowerCase()),
      );
      if (alreadyHave) continue;
      facts.push({
        label: icon || "Fact",
        value: text,
        icon: icon || "Info",
      });
    }
  }

  return facts;
});

const hasGroupedFaqs = computed(() => {
  const grouped = props.flagshipGroupedFaqs ?? {};
  return Object.values(grouped).some(
    (group) => Array.isArray(group?.faqs) && group.faqs.length > 0,
  );
});

const scrollToTrekPackages = () => {
  const trekPackages = document.getElementById("trekPackages");
  if (trekPackages) {
    trekPackages.scrollIntoView({ behavior: "smooth" });
  }
};

const featureImageAlt = computed(() =>
  resolveImageAlt(page.value?.featureImage?.alt, trekPackagesTitle.value),
);
</script>

<template>
  <AppTrekFeaturePhotos />
  <div class="content">
    <AppNavBreadcrumb />
    <section v-if="page?.trek_intro" class="treks-intro-section">
      <div class="container">
        <div class="col">
          <section v-if="page?.featureVideos?.length" id="featureVideos">
            <AppFeatureVideos />
          </section>
          <section v-else-if="page?.featureImage" id="featureImage">
            <NuxtPicture
              v-if="page?.featureImage"
              loading="lazy"
              :src="page?.featureImage.url"
              :width="page?.featureImage.width"
              :height="page?.featureImage.height"
              :alt="featureImageAlt"
              sizes="sm:100vw md:50vw lg:900px"
            />
          </section>
        </div>
        <div class="col">
          <div v-html="page?.trek_intro" />
          <KirbyLayouts :layouts="introLayouts" />
          <button class="btn" @click="scrollToTrekPackages">
            View Trek Packages
          </button>
        </div>
      </div>
    </section>

    <section
      v-if="keyFacts.length"
      class="trek-key-facts"
      aria-labelledby="trek-key-facts-heading"
    >
      <div class="container">
        <h2 id="trek-key-facts-heading">Key facts</h2>
        <dl class="trek-key-facts__list">
          <div v-for="fact in keyFacts" :key="fact.label" class="trek-key-facts__item">
            <div class="trek-key-facts__icon" aria-hidden="true">
              <AppSVGBrandIconsAll :icon-name="fact.icon" />
            </div>
            <div class="trek-key-facts__text">
              <dt>{{ fact.label }}</dt>
              <dd>{{ fact.value }}</dd>
            </div>
          </div>
        </dl>
      </div>
    </section>

    <section
      class="treks-intro-section2"
      :class="!page?.trek_intro ? 'add-padding' : ''"
    >
      <div class="container">
        <AppTrekUsefulInfo :trek-page="page?.trekPage" :trek-uri="page?.uri" />
      </div>
    </section>
    <section id="trekPackages" class="trek-packages">
      <div class="container">
        <header>
          <h3>Trek Packages</h3>
        </header>

        <AppTrekPackagesApi :trek-title="trekPackagesTitle" />
      </div>
    </section>

    <section
      v-if="hasGroupedFaqs"
      class="trek-flagship-faqs"
      aria-labelledby="trek-faqs-heading"
    >
      <div class="container">
        <div class="trek-flagship-faqs__inner">
          <h2 id="trek-faqs-heading">Frequently asked questions</h2>
          <AppTrekFaqs :faqs="flagshipGroupedFaqs ?? {}" />
        </div>
      </div>
    </section>

    <AppGalleryListTreksPage />
    <AppFeaturedArticlesTrekPage :trek-title="trekPackagesTitle" />
  </div>
</template>

<style scoped>
.trek-flagship-faqs__inner :deep(#faqCats) {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem 2rem;
  max-width: 100%;
  width: 100%;
  margin: 1.25rem 0 0;
  align-items: start;
}

.trek-flagship-faqs__inner :deep(#faqCats .faq-item) {
  width: 100%;
  margin: 0;
}

.trek-flagship-faqs__inner :deep(#faqCats .faq-item header) {
  align-items: flex-start;
  min-height: 3.25rem;
}

.trek-flagship-faqs__inner :deep(#faqCats .faq-item header .faq-icon) {
  flex: 0 0 50px;
  width: 50px;
  height: 50px;
}

.trek-flagship-faqs__inner :deep(#faqCats .faq-item header .faq-icon svg) {
  width: 100%;
  height: 100%;
  display: block;
}

.trek-flagship-faqs__inner :deep(#faqCats .faq-item header h2) {
  line-height: 1.15;
  margin: 0;
}

@media screen and (min-width: 992px) {
  .trek-flagship-faqs__inner :deep(#faqCats) {
    grid-template-columns: 1fr 1fr;
  }

  /* Subgrid keeps category titles on the same row across columns */
  .trek-flagship-faqs__inner :deep(#faqCats .faq-item) {
    display: grid;
    grid-template-rows: subgrid;
    grid-row: span 2;
    row-gap: 1rem;
    align-items: start;
  }
}
</style>
